import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Centralized axios client — token auto-attached, 401 auto-logout
export const apiClient = axios.create({ baseURL: API_URL });

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Avoid redirect loop on /login itself
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Categories
export const getCategories = () => apiClient.get('/categories');
export const createCategory = (data) => apiClient.post('/categories', data);
export const updateCategory = (id, data) => apiClient.put(`/categories/${id}`, data);
export const deleteCategory = (id) => apiClient.delete(`/categories/${id}`);

// Transactions
export const getTransactions = (params = {}) => {
  const query = new URLSearchParams();
  if (params.month) query.append('month', params.month);
  if (params.year) query.append('year', params.year);
  if (params.type) query.append('type', params.type);
  if (params.scope) query.append('scope', params.scope);
  return apiClient.get(`/transactions?${query.toString()}`);
};
export const createTransaction = (data) => apiClient.post('/transactions', data);
export const updateTransaction = (id, data) => apiClient.put(`/transactions/${id}`, data);
export const deleteTransaction = (id) => apiClient.delete(`/transactions/${id}`);

// Budgets
export const getBudgets = (month, year) => apiClient.get(`/budgets?month=${month}&year=${year}`);
export const createOrUpdateBudget = (data) => apiClient.post('/budgets', data);
export const copyBudgets = (fromMonth, fromYear, toMonth, toYear) =>
  apiClient.post(`/budgets/copy?from_month=${fromMonth}&from_year=${fromYear}&to_month=${toMonth}&to_year=${toYear}`);

// Goals
export const getGoals = () => apiClient.get('/goals');
export const createGoal = (data) => apiClient.post('/goals', data);
export const updateGoal = (id, data) => apiClient.put(`/goals/${id}`, data);
export const deleteGoal = (id) => apiClient.delete(`/goals/${id}`);

// Recurring Bills
export const getRecurringBills = () => apiClient.get('/recurring-bills');
export const getUpcomingBills = (days = 7) => apiClient.get(`/recurring-bills/upcoming?days=${days}`);
export const createRecurringBill = (data) => apiClient.post('/recurring-bills', data);
export const updateRecurringBill = (id, data) => apiClient.put(`/recurring-bills/${id}`, data);
export const deleteRecurringBill = (id) => apiClient.delete(`/recurring-bills/${id}`);

// Dashboard
export const getMonthlyDashboard = (month, year, scope = 'all') => apiClient.get(`/dashboard/monthly?month=${month}&year=${year}&scope=${scope}`);
export const getAnnualDashboard = (year) => apiClient.get(`/dashboard/annual?year=${year}`);
export const getDashboardInsights = (month, year, scope = 'all') => apiClient.get(`/dashboard/insights?month=${month}&year=${year}&scope=${scope}`);
export const getMonthlyDetail = (month, year, scope = 'all') => apiClient.get(`/dashboard/monthly-detail?month=${month}&year=${year}&scope=${scope}`);
export const getDashboardByMember = (month, year, scope = 'all') => apiClient.get(`/dashboard/by-member?month=${month}&year=${year}&scope=${scope}`);

// Family (member-level)
export const getMyFamily = () => apiClient.get('/family');
export const getFamilyMembers = () => apiClient.get('/family/members');

// Import / Export
export const importExcel = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return apiClient.post('/import/excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const downloadExport = async (format, month, year) => {
  const params = new URLSearchParams({ format });
  if (month) params.append('month', month);
  if (year) params.append('year', year);
  const res = await apiClient.get(`/export/transactions?${params}`, { responseType: 'blob' });
  return res.data;
};
export const downloadPdfReport = async (type, month, year) => {
  const params = new URLSearchParams({ type });
  if (month) params.append('month', month);
  if (year) params.append('year', year);
  const res = await apiClient.get(`/reports/pdf?${params}`, { responseType: 'blob' });
  return res.data;
};

// Admin
export const adminGetStats = () => apiClient.get('/admin/stats');
export const adminGetUsers = () => apiClient.get('/admin/users');
export const adminGetFamilies = (search) => apiClient.get(`/admin/families${search ? `?search=${encodeURIComponent(search)}` : ''}`);
export const adminGetFamilyDetail = (familyId) => apiClient.get(`/admin/families/${familyId}`);
export const adminDownloadFamilyReport = async (familyId, year) => {
  const params = year ? `?year=${year}` : '';
  const res = await apiClient.get(`/admin/families/${familyId}/report${params}`, { responseType: 'blob' });
  return res.data;
};

// Admin · AI Providers
export const adminGetAIProviders = () => apiClient.get('/admin/ai-providers');
export const adminCreateAIProvider = (data) => apiClient.post('/admin/ai-providers', data);
export const adminUpdateAIProvider = (id, data) => apiClient.put(`/admin/ai-providers/${id}`, data);
export const adminDeleteAIProvider = (id) => apiClient.delete(`/admin/ai-providers/${id}`);
export const adminTestAIProvider = (id) => apiClient.post(`/admin/ai-providers/${id}/test`);
export const adminActivateAIProvider = (id) => apiClient.put(`/admin/ai-providers/${id}/activate`);

// Admin · Quant Lab bridge (Fase 4 — read-only del vault locale)
export const quantHealth = () => apiClient.get('/admin/quant/health');
export const quantDashboard = () => apiClient.get('/admin/quant/dashboard');
export const quantListStrategies = () => apiClient.get('/admin/quant/strategies');
export const quantGetStrategy = (name) => apiClient.get(`/admin/quant/strategies/${name}`);
export const quantListAssets = () => apiClient.get('/admin/quant/assets');
export const quantGetAsset = (symbol) => apiClient.get(`/admin/quant/assets/${encodeURIComponent(symbol)}`);
export const quantListSessions = () => apiClient.get('/admin/quant/sessions');
export const quantGetSession = (slug) => apiClient.get(`/admin/quant/sessions/${slug}`);
export const quantListDataSources = () => apiClient.get('/admin/quant/data-sources');
export const quantGetDataSource = (slug) => apiClient.get(`/admin/quant/data-sources/${slug}`);
export const quantGetMasterRules = () => apiClient.get('/admin/quant/rules/master');
export const quantGetChangelog = () => apiClient.get('/admin/quant/rules/changelog');
export const quantKgSummary = () => apiClient.get('/admin/quant/kg/summary');
export const quantFigureUrl = (filename) => `${API_URL}/admin/quant/figures/${filename}`;

// Quant Lab — series JSON (OHLCV + segnali + equity per Lightweight Charts)
export const quantListSeries = () => apiClient.get('/admin/quant/series');
export const quantGetSeries = (strategy, asset) =>
  apiClient.get(`/admin/quant/series/${encodeURIComponent(strategy)}/${encodeURIComponent(asset)}`);
export const quantKgRaw = () => apiClient.get('/admin/quant/kg/raw');
export const quantListStrategySpecs = () => apiClient.get('/admin/quant/strategies/specs/all');
export const quantStrategyComparison = (name) =>
  apiClient.get(`/admin/quant/strategies/${encodeURIComponent(name)}/comparison`);
export const quantUniverse = () => apiClient.get('/admin/quant/universe');
export const quantPromptDispatch = (payload) =>
  apiClient.post('/admin/quant/prompt/dispatch', payload);
export const quantListAssetPrices = () => apiClient.get('/admin/quant/asset-prices');
export const quantGetAssetPrices = (symbol) =>
  apiClient.get(`/admin/quant/asset-prices/${encodeURIComponent(symbol)}`);
export const quantAssetApplicability = (symbol) =>
  apiClient.get(`/admin/quant/asset-prices/${encodeURIComponent(symbol)}/applicability`);
export const quantSimulate = (payload) =>
  apiClient.post('/admin/quant/simulate', payload);
export const quantAIProvidersAvailable = () => apiClient.get('/admin/quant/ai/providers/available');
export const quantAIExecute = (payload) =>
  apiClient.post('/admin/quant/ai/execute', payload);
// Runna backtest+export delle serie per UNA strategia (post-AI o manuale)
export const quantRegenerateSeries = (name) =>
  apiClient.post(`/admin/quant/strategies/${encodeURIComponent(name)}/regenerate-series`);

// Paper trading Alpaca — account, posizioni, ordini, trades, stats
export const quantPaperAccount = () =>
  apiClient.get('/admin/quant/paper/account');
export const quantPaperPositions = () =>
  apiClient.get('/admin/quant/paper/positions');
export const quantPaperPlaceOrder = (payload) =>
  apiClient.post('/admin/quant/paper/orders', payload);
export const quantPaperClosePosition = (symbol) =>
  apiClient.post(`/admin/quant/paper/positions/${encodeURIComponent(symbol)}/close`);
export const quantPaperTrades = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null) q.append(k, v); });
  return apiClient.get(`/admin/quant/paper/trades?${q.toString()}`);
};
export const quantPaperStats = () =>
  apiClient.get('/admin/quant/paper/stats');

// Live signal — verdetto strategy+symbol dalla logica quant (poll 30s)
export const quantLiveSignal = (strategy, symbol) =>
  apiClient.get(`/admin/quant/live/signal/${encodeURIComponent(strategy)}/${encodeURIComponent(symbol)}`);

// Live mode via Alpaca: latest quote (real-time IEX) + bars intraday
export const quantLiveQuote = (symbol, feed = 'iex') =>
  apiClient.get(`/admin/quant/live/quote/${encodeURIComponent(symbol)}?feed=${feed}`);
export const quantLiveBars = (symbol, params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null) q.append(k, v); });
  return apiClient.get(`/admin/quant/live/bars/${encodeURIComponent(symbol)}?${q.toString()}`);
};

// Market providers — catalog
export const adminMarketProviderCatalog = () => apiClient.get('/admin/market-providers/catalog');
export const adminActivateNoAuthProvider = (kind) =>
  apiClient.post('/admin/market-providers/activate-no-auth', { kind });

// Admin · Market Providers
export const adminGetMarketProviders = () => apiClient.get('/admin/market-providers');
export const adminCreateMarketProvider = (data) => apiClient.post('/admin/market-providers', data);
export const adminUpdateMarketProvider = (id, data) => apiClient.put(`/admin/market-providers/${id}`, data);
export const adminDeleteMarketProvider = (id) => apiClient.delete(`/admin/market-providers/${id}`);
export const adminTestMarketProvider = (id) => apiClient.post(`/admin/market-providers/${id}/test`);
export const adminActivateMarketProvider = (id, active = true) =>
  apiClient.put(`/admin/market-providers/${id}/activate?active=${active}`);

// Estrae un messaggio leggibile da un errore axios — gestisce stringhe, oggetti
// e liste di errori Pydantic v2 (`[{type, loc, msg, input, ctx, url}]`).
export const getErrorMessage = (err, fallback = 'Errore') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d && typeof d === 'object' ? (d.msg || JSON.stringify(d)) : String(d))).join(' · ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return fallback;
};

// === Sprint 2 — Auto-trader ===
export const quantAutoTraderStatus = () => apiClient.get('/admin/quant/auto-trader/status');
export const quantAutoTraderToggle = (enabled) => apiClient.post('/admin/quant/auto-trader/toggle', { enabled });
export const quantAutoTraderDecisions = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v != null) q.append(k, v); });
  return apiClient.get(`/admin/quant/auto-trader/decisions?${q.toString()}`);
};

// === Sprint 2 — Performance live ===
export const quantStrategyPerformanceLive = (name, window = 30) =>
  apiClient.get(`/admin/quant/strategies/${encodeURIComponent(name)}/performance/live?window=${window}`);

// === Sprint 2 — Walk-forward ===
export const quantWalkForward = (name) =>
  apiClient.get(`/admin/quant/strategies/${encodeURIComponent(name)}/walk-forward`);
export const quantWalkForwardRun = (name, payload) =>
  apiClient.post(`/admin/quant/strategies/${encodeURIComponent(name)}/walk-forward/run`, payload);

// === Sprint 2 — Weekly review ===
export const quantWeeklyReviewLatest = () => apiClient.get('/admin/quant/weekly-review/latest');
export const quantWeeklyReviewRunNow = () => apiClient.post('/admin/quant/weekly-review/run-now');

// === Sprint 4.5 — Quant public (user-facing, non admin) ===
export const quantUniversePublic = () => apiClient.get('/quant/universe');
export const quantStrategiesPublic = () => apiClient.get('/quant/strategies');
export const quantAIProvidersAvailablePublic = () => apiClient.get('/quant/ai-providers/available');
export const quantPriceAt = (symbol, date) =>
  apiClient.get(`/quant/price/${encodeURIComponent(symbol)}/at?date=${date}`);
export const quantPriceLatest = (symbol) =>
  apiClient.get(`/quant/price/${encodeURIComponent(symbol)}/latest`);

// === Sprint 3 (post-pivot 2026-05-18) — Portfolios + Fees profiles ===
export const listPortfolios = () => apiClient.get('/portfolios');
export const getPortfolio = (id) => apiClient.get(`/portfolios/${id}`);
export const createPortfolio = (data) => apiClient.post('/portfolios', data);
export const updatePortfolio = (id, data) => apiClient.patch(`/portfolios/${id}`, data);
export const deletePortfolio = (id) => apiClient.delete(`/portfolios/${id}`);
export const getPortfolioStats = (id) => apiClient.get(`/portfolios/${id}/stats`);

export const listPortfolioTrades = (id, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiClient.get(`/portfolios/${id}/trades${qs ? '?' + qs : ''}`);
};
export const openPortfolioOrder = (id, data) => apiClient.post(`/portfolios/${id}/orders`, data);
export const closePortfolioOrder = (id, tradeId, data) =>
  apiClient.post(`/portfolios/${id}/orders/${tradeId}/close`, data);
export const addPastOperation = (id, data) =>
  apiClient.post(`/portfolios/${id}/past-operations`, data);
export const portfolioAIAnalyze = (id, data) =>
  apiClient.post(`/portfolios/${id}/ai-analyze`, data);
export const listLinkedStrategies = (id) =>
  apiClient.get(`/portfolios/${id}/strategies`);
export const portfolioHistory = (id) =>
  apiClient.get(`/portfolios/${id}/history`);

export const listFeesPresets = () => apiClient.get('/fees-profiles/presets');
export const listFeesProfiles = () => apiClient.get('/fees-profiles');
export const createFeesProfile = (data) => apiClient.post('/fees-profiles', data);
export const createFeesProfileFromPreset = (presetId) =>
  apiClient.post(`/fees-profiles/from-preset/${presetId}`);
export const deleteFeesProfile = (id) => apiClient.delete(`/fees-profiles/${id}`);

export const PORTFOLIO_TYPES = [
  { value: 'synthetic_from_scratch', label: 'Sintetico (from scratch)',
    description: 'Budget iniziale + asset + strategia. Il sistema simula tutto.' },
  { value: 'synthetic_live', label: 'Sintetico live (paper)',
    description: 'Auto-trader interno esegue segnali in tempo reale.' },
  { value: 'real_reconstructed', label: 'Reale ricostruito',
    description: 'Inserisci a mano le operazioni del tuo broker per tracciare PnL.' },
];

// Utility functions
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

export const TRANSACTION_TYPES = [
  { value: 'REDDITO', label: 'Reddito', color: 'emerald' },
  { value: 'SPESE', label: 'Spese', color: 'red' },
  { value: 'RISPARMI', label: 'Risparmi', color: 'blue' },
  { value: 'DEBITI', label: 'Debiti', color: 'orange' }
];

export const TYPE_COLORS = {
  REDDITO: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', chart: 'hsl(150, 60%, 45%)' },
  SPESE: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', chart: 'hsl(0, 84%, 60%)' },
  RISPARMI: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', chart: 'hsl(190, 70%, 40%)' },
  DEBITI: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', chart: 'hsl(25, 80%, 60%)' }
};
